import { gameInfoType } from '@op/types/common';
import React, { useState, useMemo, useRef } from 'react';
import { Dropdown, Button, SearchBox, List } from 'tea-component';
import { useIntl } from 'umi';
import {
  metricsList,
  MetricsType,
  getAllMetricsList,
  metricsIconMap,
  getMetricsList,
  MetricsItemType,
  getMetricsItemFunc,
} from './config';
import NoData from '@op/components/NoDataCommon';
import { highlight } from '../../utils';
import { useStateRef } from '@op/hooks/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import BubbleCommon from '@op/components/BubbleCommon';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';

function RenderItem({
  item,
  formatMessage,
  searchText,
  showSplitText = false,
}: {
  item: MetricsItemType;
  formatMessage: any;
  searchText?: string;
  showSplitText?: boolean;
}) {
  const icon = metricsIconMap[item?.icon || ''];
  const text = formatMessage({ id: item.text });
  return (
    <span key={item.value}>
      {icon && <img src={icon}></img>}
      {searchText ? highlight(text, searchText) : text}
      {showSplitText ? ', ' : ''}
    </span>
  );
}

export default function MetricsOptions({
  gameInfo,
  selectedMetricsList,
  setSelectedMetricsList,
  metricsValue,
  resetClick,
  confirmClick,
  clearMetrics,
  changeSelect,
}: {
  gameInfo: gameInfoType;
  selectedMetricsList: Array<string>;
  setSelectedMetricsList: (value: Array<string>) => void;
  metricsValue: MetricsType | 'Customized';
  resetClick: () => void;
  confirmClick: () => void;
  clearMetrics: () => void;
  changeSelect: () => void;
}) {
  const { formatMessage } = useIntl();
  const [allOptions, getMetricsItem] = useMemo(() => {
    const entity_type: any = gameInfo.entity_type;
    const allOptions = getAllMetricsList(entity_type);

    const getMetricsItem = getMetricsItemFunc(entity_type);

    return [allOptions, getMetricsItem];
  }, [gameInfo]);

  const [selectedMetricsListButton, selectedLength] = useMemo(() => {
    return [
      selectedMetricsList.map((item) => getMetricsItem(item)).filter(Boolean),
      selectedMetricsList.length,
    ];
  }, [selectedMetricsList]);

  const isSelected = (value: string) => {
    return selectedMetricsList.includes(value);
  };

  function onChange(value: string, onlySelected: boolean = false) {
    changeSelect();
    const isFlag = isSelected(value);
    if (isFlag && onlySelected) return;

    if (isFlag) {
      return setSelectedMetricsList(
        selectedMetricsList.filter((item) => item !== value),
      );
    }
    setSelectedMetricsList([...selectedMetricsList, value]);
  }

  // 如果是Confirm按钮点击，则不重置
  const stopRef = useRef(false);
  const MergeChannelIconElement = useMetricsChannel({ gameInfo });

  return (
    <Dropdown
      trigger="click"
      className="dbo-compare-select-opinions"
      overlayClassName="dbo-metric-select-overlay"
      appearance="button"
      clickClose={false}
      matchButtonWidth
      style={{ marginRight: 10 }}
      onOpen={() => buriedBtnPoint('EditMetrics')}
      onClose={() => {
        if (!stopRef.current) resetClick();
        stopRef.current = false;
      }}
      button={
        <div className="select-button">
          <span className="select-button-text">
            {formatMessage({ id: metricsValue })} ({selectedMetricsList.length})
          </span>
          <span className="select-button-value">
            {selectedMetricsListButton.map((item, index) => (
              <RenderItem
                key={item.value}
                item={item}
                formatMessage={formatMessage}
                showSplitText={index !== selectedLength - 1}
              />
            ))}
          </span>
        </div>
      }
    >
      {(close) => (
        <div className="dbo-metric-box">
          <SearchBoxElement allOptions={allOptions} onChange={onChange} />

          <div className="dbo-metric-form">
            <div className="dbo-metric-row">
              <div className="dbo-metric-label">
                <span>
                  {formatMessage({ id: 'Metric' })} (
                  {selectedMetricsList.length})
                </span>
              </div>
              <div className="dbo-metric-context">
                <span className="dbo-metric-clear" onClick={clearMetrics}>
                  {formatMessage({
                    id:
                      selectedMetricsList.length === 0
                        ? 'Select All'
                        : 'Clear All',
                  })}
                </span>
              </div>
            </div>
            {metricsList.map((key: MetricsType) => (
              <MetricsItem
                key={key}
                gameInfo={gameInfo}
                valueKey={key}
                title={key}
                onChange={onChange}
                isSelected={isSelected}
                MergeChannelIconElement={MergeChannelIconElement}
              ></MetricsItem>
            ))}
          </div>
          <div className="dbo-metric-footer">
            <Button
              type="primary"
              onClick={() => {
                buriedBtnPoint('ConfirmEditedMetrics', {
                  value: selectedMetricsList,
                });
                stopRef.current = true;
                confirmClick();
                close();
              }}
              disabled={selectedMetricsList.length === 0}
            >
              {formatMessage({ id: 'Confirm' })}
            </Button>
            <Button type="weak" onClick={resetClick}>
              {formatMessage({ id: 'Reset' })}
            </Button>
          </div>
        </div>
      )}
    </Dropdown>
  );
}

function SearchBoxElement({
  allOptions,
  onChange,
}: {
  allOptions: Array<MetricsItemType>;
  onChange: (value: string, onlySelected: boolean) => void;
}) {
  const [valueRef, value, setValue] = useStateRef('');
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    if (!value) return [];
    return allOptions.filter((it) => {
      const textEn = (it.text_en || it.text).toLowerCase();
      const textZh = (it.text_zh || it.text).toLowerCase();
      const val = value.toLocaleLowerCase();

      return textEn.includes(val) || textZh.includes(val);
    });
  }, [value]);

  return (
    <Dropdown
      appearance="button"
      clickClose={false}
      open={open}
      appearance="pure"
      style={{ width: '100%' }}
      matchButtonWidth
      button={
        <SearchBox
          value={value}
          onFocus={() => valueRef.current && setOpen(true)}
          onBlur={() => !valueRef.current && setOpen(false)}
          onChange={(val: string) => {
            setValue(val);
            if (val) setOpen(true);
            else setOpen(false);
          }}
          placeholder={formatMessage({ id: 'Search any metric in the table' })}
        />
      }
    >
      <List className="tea-list--option tea-list--option--search">
        {options.length > 0 ? (
          options.map((item) => {
            return (
              <List.Item
                key={item.value}
                onClick={() => {
                  setValue('');
                  onChange(item.value, true);
                  setOpen(false);
                }}
              >
                <RenderItem
                  formatMessage={formatMessage}
                  item={item}
                  searchText={value}
                />
              </List.Item>
            );
          })
        ) : (
          <NoData style={{ padding: 20 }} hasTitle></NoData>
        )}
      </List>
    </Dropdown>
  );
}

function MetricsItem({
  valueKey,
  gameInfo,
  title,
  onChange,
  isSelected,
  MergeChannelIconElement,
}: {
  valueKey: MetricsType;
  gameInfo: gameInfoType;
  title: string;
  isSelected: (value: string) => boolean;
  onChange: (value: string) => void;
  MergeChannelIconElement?: any;
}) {
  const { formatMessage } = useIntl();
  const options = useMemo(
    () => getMetricsList(valueKey, gameInfo.entity_type as any),
    [gameInfo],
  );

  return (
    <div className="dbo-metric-row">
      <div className="dbo-metric-label">{formatMessage({ id: title })}</div>
      <div className="dbo-metric-context">
        {options.map((item, index) => (
          <div className="dbo-metric-tag-list" key={index}>
            {item.map((it) => {
              if (it.isSeparate) {
                return <div key={it.value} className="dbo-separate-item"></div>;
              }
              return (
                <BubbleCommon
                  placement="top"
                  tipsMap={it?.tips}
                  showIcon={false}
                  key={it.value}
                  extraContent={
                    <MergeChannelIconElement
                      metric={it.value}
                    ></MergeChannelIconElement>
                  }
                >
                  <div
                    onClick={() => onChange(it.value)}
                    className={`dbo-metric-tag-item ${
                      isSelected(it.value) ? '--active' : ''
                    }`}
                  >
                    <RenderItem formatMessage={formatMessage} item={it} />
                  </div>
                </BubbleCommon>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
